import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import {
  createCatalog,
  has,
  type Catalog,
  type DenialReason,
  type EntitlementPolicy,
  type EntitlementSubject,
} from "@mediatechgroup/entitlements";
import {
  getTenantEntitlements,
  ModuleEntitlement,
  Tier,
  SubscriptionStatus,
} from "../api/entitlementsAPI";
import { getAuthToken } from "../api/apiHelpers";

/**
 * The policy Club Central declares, the same one the backend's
 * entitlementService passes: the tier ladder, and no subscription row
 * means no module.
 */
export const ENTITLEMENT_POLICY: EntitlementPolicy = {
  tiers: ["starter", "growth", "pro", "enterprise"],
  missingSubscription: "deny",
};

export interface ModuleEntitlementResult {
  granted: boolean;
  /** The package's vocabulary; the payload's own rows yield tier, addon or no_subscription. */
  reason?: DenialReason;
  gating?: "hard" | "soft" | "frontend_only" | "marketing_only";
  requiredTier?: Tier | null;
  requiredAddon?: string | null;
  /** The module catalog row, or undefined when the key isn't in the catalog. */
  module?: ModuleEntitlement;
}

export interface EntitlementContextType {
  tier: Tier | null;
  status: SubscriptionStatus | null;
  addons: string[];
  modules: ModuleEntitlement[];
  loading: boolean;
  /**
   * Check a single module by key. A key the catalog does not hold is
   * denied (reason "unknown_module", gating "hard"), the same as the
   * backend's requireModule: a typo must not show a surface the server
   * will then refuse.
   */
  isModuleEntitled: (moduleKey: string) => ModuleEntitlementResult;
  refresh: () => Promise<void>;
}

const defaultContext: EntitlementContextType = {
  tier: null,
  status: null,
  addons: [],
  modules: [],
  loading: true,
  isModuleEntitled: () => ({ granted: true }),
  refresh: async () => {},
};

export const EntitlementContext =
  createContext<EntitlementContextType>(defaultContext);

interface EntitlementProviderProps {
  children: ReactNode;
}

/**
 * The boot payload's rows in the package's shape. The first row wins for
 * a key listed twice, as the old `modules.find` did; the catalog never
 * holds one (module_key is unique), and a throw here would blank the app.
 */
function catalogOf(modules: ModuleEntitlement[]): Catalog {
  const seen = new Set<string>();
  const rows = [];
  for (const m of modules) {
    if (seen.has(m.module_key)) continue;
    seen.add(m.module_key);
    rows.push({
      key: m.module_key,
      requiredTier: m.required_tier,
      requiredAddon: m.required_addon,
      gating: m.gating,
    });
  }
  return createCatalog(rows);
}

/**
 * Loads the tenant's module entitlements from /api/tenant/entitlements
 * once on mount and exposes per-module grant checks.
 *
 * Sits inside AuthProvider/TenantProvider in App.tsx — needs auth +
 * tenant slug to be available before fetching. When no auth token is
 * present (public/landing pages), the provider stays in a "no
 * subscription" state and isModuleEntitled returns granted: false with
 * reason: 'no_subscription' for every catalog module.
 *
 * The decision is @mediatechgroup/entitlements' has() over the payload's
 * catalog rows and the tenant's tier and add-ons (PM 84b94246), the same
 * rule the backend decides with, so the two never disagree about a key
 * the payload holds, and a key it does not hold is denied on both sides.
 *
 * Like useHasPermission, this hook is a UX optimization — the backend's
 * requireModule middleware is the actual security boundary. Use the
 * provider state to hide nav items, render upgrade overlays, and
 * route-guard pages so users don't bump into 402s from the backend.
 *
 * See docs/FEATURE_ENTITLEMENTS.md for architecture and gating policy.
 */
export const EntitlementProvider: React.FC<EntitlementProviderProps> = ({
  children,
}) => {
  const [tier, setTier] = useState<Tier | null>(null);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [addons, setAddons] = useState<string[]>([]);
  const [modules, setModules] = useState<ModuleEntitlement[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setTier(null);
      setStatus(null);
      setAddons([]);
      setModules([]);
      setLoading(false);
      return;
    }
    try {
      const data = await getTenantEntitlements();
      setTier(data.tier);
      setStatus(data.status);
      setAddons(data.addons || []);
      setModules(data.modules || []);
    } catch {
      // Network error or 401 — treat as no entitlements. The backend
      // requireModule middleware is the actual gate; this hook is a
      // UX optimization. Falling back to "denied" prevents accidental
      // exposure of premium UI when entitlements can't be loaded: with
      // no catalog rows every key is unknown, and unknown is denied.
      setTier(null);
      setStatus(null);
      setAddons([]);
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const catalog = useMemo(() => catalogOf(modules), [modules]);
  // The backend answers tier and status together: both null when the
  // tenant has no active or trial subscription row.
  const subject = useMemo<EntitlementSubject>(
    () => ({ subscription: status === null ? null : { tier, addons } }),
    [tier, status, addons]
  );

  const isModuleEntitled = useCallback(
    (moduleKey: string): ModuleEntitlementResult => {
      const decision = has(catalog, subject, moduleKey, ENTITLEMENT_POLICY);
      const mod = modules.find((m) => m.module_key === moduleKey);
      if (decision.allowed) return { granted: true, module: mod };
      if (!mod) {
        // Unknown module key: there is no row to say how to react, so
        // hide the surface, as the backend's 402 gates it hard.
        return { granted: false, reason: "unknown_module", gating: "hard" };
      }
      return {
        granted: false,
        reason: decision.reason,
        gating: mod.gating,
        requiredTier: mod.required_tier,
        requiredAddon: mod.required_addon,
        module: mod,
      };
    },
    [catalog, subject, modules]
  );

  return (
    <EntitlementContext.Provider
      value={{
        tier,
        status,
        addons,
        modules,
        loading,
        isModuleEntitled,
        refresh,
      }}
    >
      {children}
    </EntitlementContext.Provider>
  );
};
